import { getAllQuestionnaireAnswers } from "@medplum/core";
import {
  CodeableConcept,
  Coding,
  QuestionnaireResponse,
  QuestionnaireResponseItemAnswer,
} from "@medplum/fhirtypes";

import { TIER_INSURANCE_LINK_IDS } from "@/billing/components/VerifyInsurance/constants/verify-insurance-fields";
import { getQuestionnaireAnswerValue } from "@/utils/questionnaires";

/** Matches {@link getQuestionnaireAnswerValue} return shape; kept local to avoid coupling questionnaires.ts to this PR. */
type QuestionnaireAnswerScalar = string | boolean | number | Coding;

/** Values that may be formatted as an intake answer label (intake + defensive widening). */
type IntakeAnswerScalarValue = QuestionnaireAnswerScalar | CodeableConcept | null | undefined;

function formatFromCodeableConcept(codeableConcept: CodeableConcept | undefined): string {
  if (!codeableConcept) return "";
  const text = codeableConcept.text?.trim();
  if (text) return text;
  const firstCoding = codeableConcept.coding?.[0];
  if (firstCoding?.display?.trim()) return firstCoding.display.trim();
  if (firstCoding?.code?.trim()) return firstCoding.code.trim();
  return "";
}

function formatCodingOrScalarAnswer(value: IntakeAnswerScalarValue): string {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value).trim();
  if (typeof value === "boolean") return "";

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (Array.isArray(record.coding)) {
      return formatFromCodeableConcept(value as CodeableConcept);
    }
    const coding = value as Coding;
    const display = typeof coding.display === "string" ? coding.display.trim() : "";
    const code = typeof coding.code === "string" ? coding.code.trim() : "";
    if (display || code) return display || code;
    return "";
  }

  return "";
}

/**
 * Primary insurance company as entered on the client intake billing section.
 * Used for client profile display so the label matches the latest saved intake
 * before Coverage is updated by verification.
 */
export function getPrimaryInsuranceCompanyFromIntake(
  questionnaireResponse: QuestionnaireResponse | undefined,
): string {
  if (!questionnaireResponse) return "";
  const linkId = TIER_INSURANCE_LINK_IDS.primary.company;
  const answers = getAllQuestionnaireAnswers(questionnaireResponse);
  const items = answers[linkId] ?? [];
  const firstAnswer = items[0] as
    | (QuestionnaireResponseItemAnswer & { valueCodeableConcept?: CodeableConcept })
    | undefined;
  if (firstAnswer?.valueCodeableConcept) {
    return formatFromCodeableConcept(firstAnswer.valueCodeableConcept);
  }
  const value = getQuestionnaireAnswerValue(items);
  return formatCodingOrScalarAnswer(value);
}

const PAYMENT_METHOD_LINK_ID = "billingInformationPaymentMethod";

/**
 * Expected primary form of payment as entered on the client intake billing section
 * (the `billingInformationPaymentMethod` answer, e.g. "Private Insurance"). This is the same
 * source the Billing page writes to, so the label matches what users set there — unlike the
 * legacy `forms/intake/payment-method` Observation, which the app no longer writes.
 */
export function getPaymentMethodFromIntake(
  questionnaireResponse: QuestionnaireResponse | undefined,
): string {
  if (!questionnaireResponse) return "";
  const answers = getAllQuestionnaireAnswers(questionnaireResponse);
  const items = answers[PAYMENT_METHOD_LINK_ID] ?? [];
  const firstAnswer = items[0] as
    | (QuestionnaireResponseItemAnswer & { valueCodeableConcept?: CodeableConcept })
    | undefined;
  if (firstAnswer?.valueCodeableConcept) {
    return formatFromCodeableConcept(firstAnswer.valueCodeableConcept);
  }
  const value = getQuestionnaireAnswerValue(items);
  return formatCodingOrScalarAnswer(value);
}
